/**
 * ChallengeLeaderboard — the opted-in board for one challenge (and the
 * competitive board).
 *
 * Presentational only. It renders exactly the entries the server sent: the
 * server has already removed everyone who did not opt in (to this challenge
 * AND to the public leaderboard) and replaced names with the published display
 * name or 'Anonymous'. Entries carry no user id and no score, only progress in
 * the challenge's unit. Equal progress shares a rank. Participants whose
 * results are not measured yet arrive separately (`unranked`) and are listed
 * apart — never placed on the board as zero.
 */

import type { LeaderboardEntry, UnrankedEntry } from './challengeTypes';

interface Props {
  entries: LeaderboardEntry[];
  unranked?: UnrankedEntry[];
  unit: string;
  target: number | null;
  competitive?: boolean;
}

export function ChallengeLeaderboard({ entries, unranked = [], unit, target, competitive = false }: Props) {
  if (entries.length === 0 && unranked.length === 0) {
    return (
      <p className="text-sm text-gray-500" data-testid="challenge-leaderboard-empty">
        Nobody has chosen to appear on this leaderboard yet. Participants appear only if they opt in here and their
        profile is public with scores shown.
      </p>
    );
  }
  const tied = new Set(entries.map((e) => e.rank).filter((r, i, all) => all.indexOf(r) !== i));
  return (
    <div className="space-y-3">
      {entries.length > 0 && (
        <ol className="divide-y divide-gray-100" data-testid={competitive ? 'challenge-competitive-board' : 'challenge-leaderboard'}>
          {entries.map((e, i) => (
            <li key={`${e.rank}-${i}`} className={`flex items-center justify-between py-2 ${e.isMe ? 'font-semibold' : ''}`}>
              <span className="flex items-center gap-3">
                <span className="w-8 text-right text-gray-500">
                  {tied.has(e.rank) ? `=${e.rank}` : e.rank}
                </span>
                <span>
                  {e.name}
                  {e.isMe && <span className="ml-2 text-xs text-primary-600">(you)</span>}
                </span>
              </span>
              <span className="text-sm text-gray-700">
                {e.value}
                {target !== null ? ` / ${target}` : ''} {unit}
                {e.completed && <span className="ml-2 px-2 py-0.5 rounded-full bg-green-100 text-green-800 text-xs">Done</span>}
              </span>
            </li>
          ))}
        </ol>
      )}
      {unranked.length > 0 && (
        <div data-testid="challenge-unranked">
          <p className="text-xs text-gray-500">
            Not ranked yet — results not measured. These are not counted as zero and do not count against anyone:
          </p>
          <ul className="mt-1 flex flex-wrap gap-2 text-sm">
            {unranked.map((u, i) => (
              <li key={`${u.name}-${i}`} className={`px-2 py-0.5 rounded bg-gray-100 ${u.isMe ? 'font-semibold' : ''}`}>
                {u.name}
                {u.isMe && <span className="ml-1 text-xs text-primary-600">(you)</span>}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

export default ChallengeLeaderboard;
